import {
  DEFAULT_LANGUAGE,
  getLocaleForLanguage,
  getRuntimeLanguage,
} from "../i18n";

export const LEGACY_APP_CURRENCY_STORAGE_KEY = "priceradar_currency";
export const APP_CURRENCY_STORAGE_KEY = "pennypantry_currency";

const LANGUAGE_COUNTRY = {
  "pt-BR": "BR",
  en: "US",
  es: "ES",
  it: "IT",
  fr: "FR",
  de: "DE",
  "pt-PT": "PT",
};

const IMPERIAL_DISTANCE_COUNTRIES = new Set(["US"]);

const COUNTRY_CURRENCY = {
  AR: "ARS",
  AU: "AUD",
  BO: "BOB",
  BR: "BRL",
  CA: "CAD",
  CH: "CHF",
  CL: "CLP",
  CN: "CNY",
  CO: "COP",
  DE: "EUR",
  DK: "DKK",
  ES: "EUR",
  FR: "EUR",
  GB: "GBP",
  IE: "EUR",
  IN: "INR",
  IT: "EUR",
  JP: "JPY",
  MX: "MXN",
  NL: "EUR",
  NO: "NOK",
  NZ: "NZD",
  PE: "PEN",
  PT: "EUR",
  PY: "PYG",
  SE: "SEK",
  US: "USD",
  UY: "UYU",
};

const CURRENCY_LOCALES = {
  ARS: "es-AR",
  AUD: "en-AU",
  BOB: "es-BO",
  BRL: "pt-BR",
  CAD: "en-CA",
  CHF: "de-CH",
  CLP: "es-CL",
  CNY: "zh-CN",
  COP: "es-CO",
  DKK: "da-DK",
  GBP: "en-GB",
  INR: "en-IN",
  JPY: "ja-JP",
  MXN: "es-MX",
  NOK: "nb-NO",
  NZD: "en-NZ",
  PEN: "es-PE",
  PYG: "es-PY",
  SEK: "sv-SE",
  USD: "en-US",
  UYU: "es-UY",
};

const ADDRESS_COUNTRY_MATCHERS = [
  { country: "US", terms: ["united states", "usa", "u s a", "estados unidos", "eua"] },
  { country: "BR", terms: ["brasil", "brazil"] },
  { country: "CA", terms: ["canada"] },
  { country: "MX", terms: ["mexico"] },
  { country: "GB", terms: ["united kingdom", "uk", "england", "scotland", "wales"] },
  { country: "PT", terms: ["portugal"] },
  { country: "ES", terms: ["spain", "espana", "espanha"] },
  { country: "IT", terms: ["italy", "italia"] },
  { country: "FR", terms: ["france", "franca"] },
  { country: "DE", terms: ["germany", "deutschland", "alemanha"] },
  { country: "AR", terms: ["argentina"] },
  { country: "CL", terms: ["chile"] },
  { country: "CO", terms: ["colombia"] },
  { country: "PE", terms: ["peru"] },
  { country: "UY", terms: ["uruguay"] },
  { country: "PY", terms: ["paraguay"] },
  { country: "BO", terms: ["bolivia"] },
  { country: "AU", terms: ["australia"] },
  { country: "NZ", terms: ["new zealand", "nova zelandia"] },
  { country: "JP", terms: ["japan", "japao"] },
  { country: "CN", terms: ["china"] },
  { country: "IN", terms: ["india"] },
];

function resolveUiLanguage() {
  return getRuntimeLanguage() || DEFAULT_LANGUAGE;
}

export function getDefaultCountryForLanguage(language = resolveUiLanguage()) {
  return LANGUAGE_COUNTRY[language] || LANGUAGE_COUNTRY[DEFAULT_LANGUAGE];
}

function getDefaultCurrencyForLanguage(language = resolveUiLanguage()) {
  return COUNTRY_CURRENCY[getDefaultCountryForLanguage(language)] || "USD";
}

function isSupportedCurrency(currency) {
  return Object.values(COUNTRY_CURRENCY).includes(String(currency || "").toUpperCase());
}

function getCurrencyLocale(currency) {
  const normalizedCurrency = String(currency || "").toUpperCase();
  if (normalizedCurrency === "EUR") {
    return getLocaleForLanguage(resolveUiLanguage());
  }
  return CURRENCY_LOCALES[normalizedCurrency] || getLocaleForLanguage(resolveUiLanguage());
}

function isInside(latitude, longitude, minLatitude, maxLatitude, minLongitude, maxLongitude) {
  return (
    latitude >= minLatitude &&
    latitude <= maxLatitude &&
    longitude >= minLongitude &&
    longitude <= maxLongitude
  );
}

export function inferCountryFromCoordinates(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "";
  }

  if (
    isInside(lat, lng, 24, 50, -125, -66) ||
    isInside(lat, lng, 51, 72, -170, -129) ||
    isInside(lat, lng, 18, 23, -161, -154) ||
    isInside(lat, lng, 17, 19, -68, -65)
  ) {
    return "US";
  }
  if (isInside(lat, lng, -34, 6, -74, -34)) {
    return "BR";
  }
  if (isInside(lat, lng, 41, 84, -141, -52)) {
    return "CA";
  }
  if (isInside(lat, lng, 14, 33, -118, -86)) {
    return "MX";
  }
  if (isInside(lat, lng, 49, 61, -8.5, 2.5)) {
    return "GB";
  }
  if (isInside(lat, lng, 32, 42.5, -32, -6)) {
    return "PT";
  }
  if (isInside(lat, lng, 27, 44, -19, 5)) {
    return "ES";
  }
  if (isInside(lat, lng, 41, 52, -6, 10)) {
    return "FR";
  }
  if (isInside(lat, lng, 47, 56, 5, 16)) {
    return "DE";
  }
  if (isInside(lat, lng, 35, 48, 6, 19)) {
    return "IT";
  }
  if (isInside(lat, lng, -56, -21, -74, -53)) {
    return "AR";
  }
  if (isInside(lat, lng, -56, -17, -76, -66)) {
    return "CL";
  }
  if (isInside(lat, lng, -5, 13, -82, -66)) {
    return "CO";
  }
  if (isInside(lat, lng, -19, 1, -82, -68)) {
    return "PE";
  }
  if (isInside(lat, lng, -35, -30, -59, -53)) {
    return "UY";
  }
  if (isInside(lat, lng, -28, -19, -63, -54)) {
    return "PY";
  }
  if (isInside(lat, lng, -23, -9, -70, -57)) {
    return "BO";
  }
  if (isInside(lat, lng, -44, -10, 112, 154)) {
    return "AU";
  }
  if (isInside(lat, lng, -48, -34, 166, 179)) {
    return "NZ";
  }
  if (isInside(lat, lng, 24, 46, 122, 146)) {
    return "JP";
  }
  if (isInside(lat, lng, 18, 54, 73, 135)) {
    return "CN";
  }
  if (isInside(lat, lng, 6, 37, 68, 98)) {
    return "IN";
  }

  return "";
}

export function inferCountryFromAddress(address) {
  const normalizedAddress = normalizeTextKey(address);
  if (!normalizedAddress) {
    return "";
  }

  const matched = ADDRESS_COUNTRY_MATCHERS.find(({ terms }) =>
    terms.some((term) => normalizedAddress.includes(normalizeTextKey(term)))
  );

  return matched?.country || "";
}

export function resolveCurrencyForLocation({ latitude, longitude, address, country } = {}) {
  const explicitCountry = String(country || "").trim().toUpperCase();
  const inferredCountry =
    explicitCountry ||
    inferCountryFromAddress(address) ||
    inferCountryFromCoordinates(latitude, longitude) ||
    getDefaultCountryForLanguage(resolveUiLanguage());

  return COUNTRY_CURRENCY[inferredCountry] || "";
}

export function getRuntimeCurrency() {
  if (typeof window !== "undefined") {
    const storedCurrency =
      window.localStorage?.getItem(APP_CURRENCY_STORAGE_KEY) ||
      window.localStorage?.getItem(LEGACY_APP_CURRENCY_STORAGE_KEY);
    if (isSupportedCurrency(storedCurrency)) {
      return storedCurrency.toUpperCase();
    }
  }

  return getDefaultCurrencyForLanguage(resolveUiLanguage());
}

export function setRuntimeCurrencyForLocation(location = {}) {
  const nextCurrency = resolveCurrencyForLocation(location) || getRuntimeCurrency();
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(APP_CURRENCY_STORAGE_KEY, nextCurrency);
  }
  return nextCurrency;
}

export function setRuntimeCurrencyForLanguage(language = resolveUiLanguage()) {
  const nextCurrency = getDefaultCurrencyForLanguage(language);
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(APP_CURRENCY_STORAGE_KEY, nextCurrency);
  }
  return nextCurrency;
}

export function parseOptionalNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function formatCurrency(value, options = {}) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return getNoDataLabel();
  }

  const hasLocationHints =
    typeof options === "object" &&
    options !== null &&
    (
      options.country !== undefined ||
      options.address !== undefined ||
      options.latitude !== undefined ||
      options.longitude !== undefined ||
      options.location !== undefined
    );
  const currency =
    typeof options === "string"
      ? options
      : options.currency ||
        (hasLocationHints ? resolveCurrencyForLocation(options.location || options) : "") ||
        getRuntimeCurrency();

  return parsed.toLocaleString(getCurrencyLocale(currency), {
    style: "currency",
    currency,
  });
}

export function formatUsdCurrency(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return getNoDataLabel();
  }

  return parsed.toLocaleString(getLocaleForLanguage(resolveUiLanguage()), {
    style: "currency",
    currency: "USD",
  });
}

function getNoDataLabel() {
  const labels = {
    "pt-BR": "n/d",
    en: "n/a",
    es: "n/d",
    it: "n/d",
    fr: "n/d",
    de: "k.A.",
    "pt-PT": "n/d",
  };

  return labels[resolveUiLanguage()] || labels[DEFAULT_LANGUAGE];
}

export function normalizePermissionState(value, fallback = "unsupported") {
  if (value === "granted" || value === "prompt" || value === "denied") {
    return value;
  }

  if (value === "default") {
    return "prompt";
  }

  return fallback;
}

export function normalizeTextKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();
}

export function formatCoordinate(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(6) : "";
}

export function hasValidCoordinates(latitude, longitude) {
  const parsedLatitude = Number(latitude);
  const parsedLongitude = Number(longitude);

  return (
    Number.isFinite(parsedLatitude) &&
    Number.isFinite(parsedLongitude) &&
    Math.abs(parsedLatitude) <= 90 &&
    Math.abs(parsedLongitude) <= 180
  );
}

export function usesImperialDistanceSystem({ language = resolveUiLanguage(), country, useImperial } = {}) {
  if (typeof useImperial === "boolean") {
    return useImperial;
  }
  const normalizedCountry = String(country || "").trim().toUpperCase() || getDefaultCountryForLanguage(language);
  return IMPERIAL_DISTANCE_COUNTRIES.has(normalizedCountry);
}

export function getDistanceUnitLabel(options = {}) {
  return usesImperialDistanceSystem(options) ? "mi" : "km";
}

export function formatDistanceInputValue(distanceKm, options = {}) {
  const parsedDistance = Number(distanceKm);
  const useImperial = usesImperialDistanceSystem(options);

  if (!Number.isFinite(parsedDistance)) {
    return "";
  }

  const displayDistance = useImperial ? kilometersToMiles(parsedDistance) : parsedDistance;
  if (!Number.isFinite(displayDistance)) {
    return "";
  }

  return useImperial
    ? formatRadiusMiles(displayDistance)
    : formatRadiusKilometers(displayDistance);
}

export function parseDistanceInputToKm(value, options = {}) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(String(value).replace(",", "."));
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return usesImperialDistanceSystem(options) ? milesToKilometers(parsedValue) : parsedValue;
}

export function formatAlternateDistance(distanceKm, options = {}) {
  const parsedDistance = Number(distanceKm);
  if (!Number.isFinite(parsedDistance)) {
    return getNoDataLabel();
  }

  const useImperial = usesImperialDistanceSystem(options);
  const alternateValue = useImperial ? parsedDistance : kilometersToMiles(parsedDistance);
  if (!Number.isFinite(alternateValue)) {
    return getNoDataLabel();
  }

  const formattedValue = useImperial
    ? formatRadiusKilometers(alternateValue)
    : formatRadiusMiles(alternateValue);
  const alternateUnit = useImperial ? "km" : "mi";
  return `${formattedValue} ${alternateUnit}`;
}

export function formatDistance(distanceKm, options = {}) {
  const formattedValue = formatDistanceInputValue(distanceKm, options);
  if (!formattedValue) {
    return getNoDataLabel();
  }

  return `${formattedValue} ${getDistanceUnitLabel(options)}`;
}

export function milesToKilometers(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return parsedValue * 1.609344;
}

export function kilometersToMiles(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return parsedValue / 1.609344;
}

export function formatRadiusMiles(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return "";
  }

  const roundedValue = parsedValue >= 10
    ? Math.round(parsedValue)
    : Math.round(parsedValue * 10) / 10;

  return Number.isInteger(roundedValue)
    ? String(roundedValue)
    : roundedValue.toFixed(1);
}

export function formatRadiusKilometers(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return "";
  }

  const roundedValue = parsedValue >= 10
    ? Math.round(parsedValue)
    : Math.round(parsedValue * 10) / 10;

  return Number.isInteger(roundedValue)
    ? String(roundedValue)
    : roundedValue.toFixed(1);
}

export function formatCapturedAt(value) {
  if (!value) {
    return getNoDataLabel();
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return getNoDataLabel();
  }

  return parsedDate.toLocaleString(getLocaleForLanguage(resolveUiLanguage()), {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function getPermissionLabel(permissionState) {
  const labelsByLanguage = {
    "pt-BR": {
      granted: "Permitido",
      prompt: "Pendente",
      denied: "Bloqueado",
      unsupported: "Indisponivel",
      checking: "Verificando",
    },
    en: {
      granted: "Allowed",
      prompt: "Pending",
      denied: "Blocked",
      unsupported: "Unavailable",
      checking: "Checking",
    },
    es: {
      granted: "Permitido",
      prompt: "Pendiente",
      denied: "Bloqueado",
      unsupported: "No disponible",
      checking: "Verificando",
    },
    it: {
      granted: "Consentito",
      prompt: "In attesa",
      denied: "Bloccato",
      unsupported: "Non disponibile",
      checking: "Verifica",
    },
    fr: {
      granted: "Autorise",
      prompt: "En attente",
      denied: "Bloque",
      unsupported: "Indisponible",
      checking: "Verification",
    },
    de: {
      granted: "Erlaubt",
      prompt: "Ausstehend",
      denied: "Blockiert",
      unsupported: "Nicht verfugbar",
      checking: "Prufe",
    },
    "pt-PT": {
      granted: "Permitido",
      prompt: "Pendente",
      denied: "Bloqueado",
      unsupported: "Indisponivel",
      checking: "A verificar",
    },
  };

  const labels = labelsByLanguage[resolveUiLanguage()] || labelsByLanguage[DEFAULT_LANGUAGE];
  return labels[permissionState] || labels.unsupported;
}

export function getPermissionBadgeStyle(permissionState) {
  const stylesByState = {
    granted: { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" },
    prompt: { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #93c5fd" },
    denied: { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fca5a5" },
    unsupported: { background: "#f8fafc", color: "#475569", border: "1px solid #cbd5e1" },
    checking: { background: "#f8fafc", color: "#475569", border: "1px solid #cbd5e1" },
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    ...(stylesByState[permissionState] || stylesByState.unsupported),
  };
}

export function haversineDistanceKm(lat1, lon1, lat2, lon2) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const deltaLatitude = toRadians(lat2 - lat1);
  const deltaLongitude = toRadians(lon2 - lon1);
  const a =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(deltaLongitude / 2) ** 2;

  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function resolveMarketDistance(market, referencePoint) {
  if (
    referencePoint &&
    hasValidCoordinates(market.latitude, market.longitude) &&
    hasValidCoordinates(referencePoint.latitude, referencePoint.longitude)
  ) {
    return haversineDistanceKm(
      referencePoint.latitude,
      referencePoint.longitude,
      market.latitude,
      market.longitude
    );
  }

  const apiDistance = Number(market.distance_km);
  if (Number.isFinite(apiDistance)) {
    return apiDistance;
  }

  return null;
}

export function buildOptionalAuthConfig(headers) {
  return headers ? { headers } : undefined;
}

export function extractProductsFromText(value) {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
